import { useState } from 'react'
import { Button, Modal, Select } from '@/components/ui'
import type { ConfirmationSource, ShipConfirmationTracker } from '../api/types'

const SOURCE_OPTIONS: { value: ConfirmationSource; label: string }[] = [
  { value: 'vendor_email', label: 'Vendor email' },
  { value: 'vendor_document', label: 'Vendor confirmation document' },
  { value: 'carrier_booking', label: 'Carrier / booking confirmation' },
  { value: 'phone', label: 'Phone / verbal' },
  { value: 'manual', label: 'Manual entry' },
]

export interface ConfirmShipDateModalProps {
  tracker: ShipConfirmationTracker | null
  isPending: boolean
  /** Total open lines on this PO (including this one). The "apply to all
   *  lines" checkbox only shows when there's more than one. */
  siblingLineCount: number
  /**
   * When true, this confirmation is always a bulk "whole PO ships
   * together" action — the checkbox is replaced with a static statement
   * and every submit goes through `onConfirmAll`, never `onConfirm`. Set
   * by the kanban board when the PO's *aggregate* card (not a single
   * line) is dragged to Confirmed — there's no single line to confirm in
   * that gesture, only "confirm every open line."
   */
  lockApplyAll?: boolean
  onCancel: () => void
  onConfirm: (body: { confirmed_date: string; source: ConfirmationSource; detail?: string }) => void
  onConfirmAll: (body: { confirmed_date: string; source: ConfirmationSource; detail?: string }) => void
}

/**
 * Inbound PO Shipping Confirmation Tracker — the one action on this board
 * that requires evidence, not a bare drag. A vendor's firm ship date isn't
 * "confirmed" in any useful sense unless SPI can point to what confirmed
 * it — this is the exact gap that let KCC PO1862 slip for a month and a
 * half on an unconfirmed estimate.
 *
 * Doubles as the "revise" flow (2026-08-26): calling confirm again on an
 * already-confirmed line overwrites the date — there is no separate edit
 * action, this modal just opens pre-filled with the current values and
 * relabels itself. Also carries the "apply to all lines of this PO"
 * checkbox — Chris wants a global date option alongside per-line control,
 * and the date input is the natural place to offer it.
 */
export function ConfirmShipDateModal({
  tracker,
  isPending,
  siblingLineCount,
  lockApplyAll = false,
  onCancel,
  onConfirm,
  onConfirmAll,
}: ConfirmShipDateModalProps) {
  const [confirmedDate, setConfirmedDate] = useState('')
  const [source, setSource] = useState<ConfirmationSource>('vendor_email')
  const [detail, setDetail] = useState('')
  const [applyToAll, setApplyToAll] = useState(lockApplyAll)

  // Pre-fill from the tracker's current values when it opens (or a
  // different tracker opens) — keyed on `id`, not the tracker object
  // itself, so a background refetch mid-edit doesn't stomp on the user's
  // in-progress input. Adjusting state during render (React's documented
  // pattern for this, not an effect) avoids the extra render an effect-
  // based sync would cause.
  const [prefilledForId, setPrefilledForId] = useState<number | null>(null)
  if (tracker && tracker.id !== prefilledForId) {
    setPrefilledForId(tracker.id)
    setConfirmedDate(tracker.confirmed_ship_date ?? '')
    setSource((tracker.confirmation_source as ConfirmationSource | null) ?? 'vendor_email')
    setDetail(tracker.confirmation_detail ?? '')
    setApplyToAll(lockApplyAll)
  }

  const isRevision = tracker?.stage === 'confirmed'

  if (!tracker) return null

  const canSubmit = !!confirmedDate
  const applyingAll = lockApplyAll || applyToAll

  function reset() {
    setConfirmedDate('')
    setSource('vendor_email')
    setDetail('')
    setApplyToAll(lockApplyAll)
    // So reopening the same tracker (e.g. Revise, cancel, Revise again)
    // re-runs the prefill above instead of finding a stale "already
    // prefilled" match and leaving the fields blank.
    setPrefilledForId(null)
  }

  function submit() {
    const body = { confirmed_date: confirmedDate, source, detail: detail || undefined }
    if (applyingAll) onConfirmAll(body)
    else onConfirm(body)
  }

  return (
    <Modal
      open={tracker !== null}
      onOpenChange={(next) => {
        if (!next) {
          reset()
          onCancel()
        }
      }}
      tone="info"
      title={isRevision ? 'Revise vendor ship date' : 'Confirm vendor ship date'}
      description={
        lockApplyAll ? (
          <>
            Record the vendor&apos;s firm ship date for every open line of{' '}
            <strong>{tracker.odoo_po_name}</strong>. This writes the confirmation onto every open
            Odoo PO line and posts a chatter note — only do this once the vendor has actually
            committed for the whole PO, not for another estimate revision.
          </>
        ) : isRevision ? (
          <>
            <strong>{tracker.odoo_po_name}</strong>
            {tracker.product_name ? <> — {tracker.product_name}</> : null} was already confirmed
            for <strong>{tracker.confirmed_ship_date}</strong>. Recording a new date overwrites
            it — there&apos;s no separate undo for a revision once saved, only the full Undo
            action back to Awaiting Confirmation.
          </>
        ) : (
          <>
            Record the vendor&apos;s firm ship date for <strong>{tracker.odoo_po_name}</strong>
            {tracker.product_name ? <> — {tracker.product_name}</> : null}. This writes the
            confirmation onto the Odoo PO line and posts a chatter note — only do this once the
            vendor has actually committed, not for another estimate revision (use &quot;Log
            estimate update&quot; for that).
          </>
        )
      }
      footer={
        <>
          <Button
            variant="ghost"
            onClick={() => {
              reset()
              onCancel()
            }}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={isPending}
            disabled={!canSubmit}
            onClick={submit}
          >
            {applyingAll ? 'Confirm all lines' : isRevision ? 'Revise ship date' : 'Confirm ship date'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">Confirmed ship date</span>
          <input
            type="date"
            value={confirmedDate}
            onChange={(e) => setConfirmedDate(e.target.value)}
            className="rounded-xs border border-border-default bg-surface px-2 py-1 text-sm text-primary focus:border-border-focus focus:outline-none"
          />
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">Source</span>
          <Select value={source} onChange={(e) => setSource(e.target.value as ConfirmationSource)}>
            {SOURCE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">Evidence / notes</span>
          <textarea
            value={detail}
            onChange={(e) => setDetail(e.target.value)}
            placeholder="e.g. booking #, BL number, email subject/date, who confirmed"
            rows={3}
            className="rounded-xs border border-border-default bg-surface px-2 py-1.5 text-sm text-primary placeholder:text-tertiary focus:border-border-focus focus:outline-none"
          />
        </label>

        {lockApplyAll ? (
          <div className="rounded-xs bg-surface-sunk px-2 py-1.5 text-sm text-secondary">
            This confirms all {siblingLineCount} open line{siblingLineCount === 1 ? '' : 's'} of{' '}
            {tracker.odoo_po_name} with this date — overwrites every line&apos;s date, including
            lines that already have their own.
          </div>
        ) : (
          siblingLineCount > 1 && (
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={applyToAll}
                onChange={(e) => setApplyToAll(e.target.checked)}
                className="mt-0.5"
              />
              <span className="text-secondary">
                Apply this date to all {siblingLineCount} open lines of {tracker.odoo_po_name} —
                overwrites every line&apos;s date, including lines that already have their own.
              </span>
            </label>
          )
        )}
      </div>
    </Modal>
  )
}
